import {
  assertMutationId,
  cloneWorkspaceDirectory,
  emptyWorkspaceDirectory,
  type WorkspaceCommitResult,
  type WorkspaceDirectory,
  type WorkspaceListener,
  type WorkspaceMutation,
  type WorkspaceRepository,
} from './WorkspaceRepository'

export class InMemoryWorkspaceAdapter implements WorkspaceRepository {
  private snapshot: WorkspaceDirectory
  private readonly listeners = new Set<WorkspaceListener>()

  constructor(initial: WorkspaceDirectory = emptyWorkspaceDirectory()) {
    this.snapshot = cloneWorkspaceDirectory(initial)
  }

  async load(): Promise<WorkspaceDirectory> {
    return cloneWorkspaceDirectory(this.snapshot)
  }

  async transact(expectedRevision: number, mutationId: string, mutation: WorkspaceMutation): Promise<WorkspaceCommitResult> {
    assertMutationId(mutationId)
    const prior = this.snapshot.mutationReceipts?.[mutationId]
    if (prior) return { status: 'duplicate', mutationId, revision: prior.revision, snapshot: cloneWorkspaceDirectory(prior.snapshot as WorkspaceDirectory) }
    if (expectedRevision !== this.snapshot.revision) {
      return {
        status: 'conflict', mutationId, expectedRevision, revision: this.snapshot.revision,
        snapshot: cloneWorkspaceDirectory(this.snapshot),
      }
    }

    const draft = cloneWorkspaceDirectory(this.snapshot)
    mutation(draft)
    const nextRevision = this.snapshot.revision + 1
    const nextSnapshot: WorkspaceDirectory = { ...draft, revision: nextRevision }
    const receiptSnapshot = cloneWorkspaceDirectory({ ...nextSnapshot, mutationReceipts: undefined })
    nextSnapshot.mutationReceipts = {
      ...(this.snapshot.mutationReceipts ?? {}),
      [mutationId]: { revision: nextRevision, snapshot: receiptSnapshot },
    }
    const receipt: Extract<WorkspaceCommitResult, { status: 'committed' }> = {
      status: 'committed', mutationId, revision: nextRevision, snapshot: cloneWorkspaceDirectory(nextSnapshot),
    }
    this.snapshot = nextSnapshot
    const published = cloneWorkspaceDirectory(this.snapshot)
    for (const listener of this.listeners) {
      try { listener(published) } catch { /* listener failures cannot roll back a committed transaction */ }
    }
    return receipt
  }

  subscribe(listener: WorkspaceListener): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  async exportRecovery(): Promise<WorkspaceDirectory> {
    return this.load()
  }
}
